import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { dashboardApi, propertiesApi } from '../api/endpoints';
import { toast } from 'sonner';

const Reports = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Router Driven Filter State
  const [filterActive, setFilterActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([dashboardApi.stats(), propertiesApi.list()]);
        setStats(s); setProps(p);
      } catch (e) {
        console.error('Failed to load report data', e);
        toast.error('Failed to load report data');
      } finally { setLoading(false); }
    })();
  }, []);

  // Intercept routing payload for dashboard wiring
  useEffect(() => {
    if (location.state?.filterLabel) {
      setFilterActive(location.state.filterLabel);
    } else {
      setFilterActive(null);
    }
  }, [location.state]);

  const clearFilters = () => {
    setFilterActive(null);
    navigate('.', { replace: true, state: null });
  };

  if (loading || !stats) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-16" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const max = Math.max(1, ...stats.monthlyRevenue.map(m => Math.max(m.booked, m.collected)));
  const totalLeads = stats.leadsBySource.reduce((s, x) => s + x.count, 0);
  const rs = stats.reportSummary || {};
  const monthLabel = new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-5">
      {/* Route Filter Banner */}
      {filterActive && (
        <div className="p-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg flex items-center justify-between text-xs font-medium animate-in fade-in duration-300">
          <div className="flex items-center gap-2">
            <Icons.Filter className="w-4 h-4 text-blue-600 shrink-0" />
            <span>Showing filtered results for: <strong>{filterActive}</strong></span>
          </div>
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-7 text-blue-700 hover:bg-blue-100 uppercase tracking-wider text-[10px]">
            Clear Filter
          </Button>
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-xl font-bold text-slate-900">Performance Reports</div>
            <div className="text-xs text-slate-500">{monthLabel} • All projects • Live data</div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm"><Icons.Calendar className="w-4 h-4 mr-1.5" /> Date Range</Button>
            <Button variant="outline" size="sm"><Icons.FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel</Button>
            <Button className="bg-brand-navy hover:bg-[#13294A] text-white" size="sm">
              <Icons.FileDown className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: 'Total Booked Value', v: `₹${rs.revenue_cr ?? 0} Cr`, d: `${rs.revenue_delta_pct >= 0 ? '+' : ''}${rs.revenue_delta_pct ?? 0}%`, up: (rs.revenue_delta_pct ?? 0) >= 0, ic: 'IndianRupee' },
          { l: 'Bookings', v: `${rs.bookings ?? 0}`, d: `${rs.bookings_delta_pct >= 0 ? '+' : ''}${rs.bookings_delta_pct ?? 0}%`, up: (rs.bookings_delta_pct ?? 0) >= 0, ic: 'KeyRound' },
          { l: 'Amount Collected', v: `₹${rs.collected_l ?? 0} L`, d: 'payments received', up: true, ic: 'Wallet' },
          { l: 'Lead Conversion', v: `${rs.conversion_pct ?? 0}%`, d: `avg deal ₹${rs.avg_deal_l ?? 0} L`, up: true, ic: 'TrendingUp' },
        ].map(s => {
          const Icon = Icons[s.ic];
          return (
            <Card key={s.l} className="border-slate-200 card-lift">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">{s.l}</div>
                    <div className="font-display text-2xl font-bold mt-1.5">{s.v}</div>
                    <div className={`text-xs mt-1.5 ${s.up ? 'text-emerald-600' : 'text-rose-600'}`}>{s.d}{s.d.includes('%') && !s.d.includes(' ') ? ' vs last month' : ''}</div>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-brand-gold-soft flex items-center justify-center">
                    <Icon className="w-5 h-5 text-brand-navy" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Booked Value vs Collections</CardTitle>
            <div className="text-xs text-slate-500 flex items-center gap-3">
              Last 6 months — from real bookings &amp; payments
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-brand-navy inline-block" /> Booked</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#D4B679' }} /> Collected</span>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="h-72 flex items-end gap-3" data-testid="revenue-trend-chart">
              {stats.monthlyRevenue.map(m => (
                <div key={m.m} className="flex-1 h-full flex flex-col items-center justify-end gap-1.5">
                  <div className="flex items-end gap-1 w-full justify-center flex-1">
                    <div className="w-5 rounded-t bg-brand-navy" style={{ height: `${(m.booked / max) * 100}%`, minHeight: m.booked > 0 ? 3 : 0 }} title={`Booked ₹${m.booked} Cr`} />
                    <div className="w-5 rounded-t" style={{ height: `${(m.collected / max) * 100}%`, minHeight: m.collected > 0 ? 3 : 0, background: '#D4B679' }} title={`Collected ₹${m.collected} Cr`} />
                  </div>
                  <div className="text-[11px] text-slate-500">{m.m}</div>
                  <div className="text-[10px] text-brand-navy font-semibold">₹{m.booked}Cr</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Lead Sources</CardTitle>
            <div className="text-xs text-slate-500">{totalLeads.toLocaleString()} leads total</div>
          </CardHeader>
          <CardContent className="space-y-3 pt-3">
            {stats.leadsBySource.map(s => {
              const pct = (s.count / (totalLeads || 1)) * 100;
              return (
                <div key={s.source}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700 font-medium">{s.source}</span>
                    <span className="text-slate-500">{pct.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded">
                    <div className="h-full rounded" style={{ width: `${pct}%`, background: s.color }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5 pt-3">
            {stats.salesFunnel.map((s, i) => (
              <div key={s.stage} className="flex items-center gap-3">
                <div className="w-24 text-xs text-slate-600">{s.stage}</div>
                <div className="flex-1 h-7 bg-slate-100 rounded">
                  <div
                    className="h-full rounded text-[11px] text-white font-semibold flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(s.pct, 3)}%`, background: `linear-gradient(90deg, #1B3A5C ${100 - i * 12}%, #3F6695)` }}
                  >{s.count}</div>
                </div>
                <div className="w-10 text-xs text-right text-slate-500">{s.pct}%</div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Project-wise Sales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-3">
            {props.slice(0, 6).map(p => {
              const pct = p.units ? Math.round((p.sold / p.units) * 100) : 0;
              return (
                <div key={p.id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700 font-medium truncate">{p.name}</span>
                    <span className="text-slate-500">{p.sold}/{p.units} • {pct}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded">
                    <div className="h-full rounded bg-gradient-to-r from-[#1B3A5C] to-[#C8A464]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Team Performance (live) */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Team Performance</CardTitle>
          <div className="text-xs text-slate-500">Live counts per team member across leads, calls, visits and bookings</div>
        </CardHeader>
        <CardContent className="pt-2 overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-performance-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400 font-bold border-b border-slate-100">
                <th className="py-2 pr-4">Member</th>
                <th className="py-2 pr-4">Leads</th>
                <th className="py-2 pr-4">Calls</th>
                <th className="py-2 pr-4">Site Visits</th>
                <th className="py-2">Bookings</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(stats.teamPerformance || []).map(t => (
                <tr key={t.name} className="hover:bg-slate-50/60">
                  <td className="py-2.5 pr-4 font-semibold text-slate-700">{t.name}</td>
                  <td className="py-2.5 pr-4">{t.leads}</td>
                  <td className="py-2.5 pr-4">{t.calls}</td>
                  <td className="py-2.5 pr-4">{t.visits}</td>
                  <td className="py-2.5">
                    <Badge variant="outline" className={t.bookings > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : ''}>{t.bookings}</Badge>
                  </td>
                </tr>
              ))}
              {(!stats.teamPerformance || stats.teamPerformance.length === 0) && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-400">No activity recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;